/**
 * Where a fan is sent back to after a round trip through Auth0.
 *
 * Pure functions of a candidate URL and the site's own base, so both are exported and tested. The
 * SDK carries the value through `appState`, which means it never touches a query string and never
 * needs the cookie `auth0-login.php` used as a backup.
 *
 * **The validation is stricter than the one it replaces, on purpose.** `auth0-login.php` compared
 * `parse_url($return_to, PHP_URL_HOST)` against `site_url()`'s host and nothing else — no scheme,
 * no port, no path restriction — so `http://` for an `https://` site passed, and so did any port.
 * Comparing whole origins is one expression and closes all three.
 */

/**
 * Parameters a login round trip adds, which must never accumulate in a return URL.
 *
 * `auth0_login` and `auth0_success` are `auth0-login.php`'s and are kept here for as long as any
 * cached HTML can still carry them: the header linked to `?auth0_login=1` for years, and an edge
 * cache holds that markup for up to an hour after a deploy.
 */
export const LOGIN_PARAMS = Object.freeze( [ 'auth0_login', 'auth0_success' ] );

/**
 * Parameters Auth0 puts on the callback URL.
 *
 * Stripped only when cleaning the callback itself, never from an arbitrary page: `code` and `state`
 * are ordinary words and a content page is entitled to use them for something else.
 */
export const CALLBACK_PARAMS = Object.freeze( [ 'code', 'state', 'error', 'error_description' ] );

/**
 * A URL with the named query parameters removed.
 *
 * @param {string}   href  An absolute URL.
 * @param {string[]} names Parameters to drop.
 * @return {string} The URL without them, or `href` unchanged when it will not parse.
 */
export function withoutParams( href, names ) {
	let url;

	try {
		url = new URL( href );
	} catch ( error ) {
		return href;
	}

	names.forEach( name => url.searchParams.delete( name ) );

	return url.toString();
}

/**
 * A return URL we are willing to navigate to, or the base.
 *
 * **Same origin, not same host.** Scheme and port are part of the comparison, so an `http://`
 * candidate for an `https://` site is refused rather than downgrading the fan.
 *
 * A relative candidate is resolved against the base and therefore always passes, which is wanted:
 * `/loyalty-join/` is the common case and cannot point anywhere else. Anything with a scheme
 * `new URL` accepts but we do not serve — `javascript:`, `data:`, another site — falls back.
 *
 * @param {string} candidate The requested return URL. May be relative, empty, or hostile.
 * @param {string} base      An absolute URL on this site, from the published config.
 * @return {string} An absolute same-origin URL. Never empty, never off-site.
 */
export function safeReturnTo( candidate, base ) {
	let home;

	try {
		home = new URL( base );
	} catch ( error ) {
		// No usable base means no usable answer. Returning the candidate here would be the one
		// path that lets an unvalidated URL through, so it returns nothing instead and the caller
		// falls back to the current document.
		return '';
	}

	if ( typeof candidate !== 'string' || candidate === '' ) {
		return home.toString();
	}

	let target;

	try {
		target = new URL( candidate, home );
	} catch ( error ) {
		return home.toString();
	}

	if ( target.origin !== home.origin ) {
		return home.toString();
	}

	return withoutParams( target.toString(), LOGIN_PARAMS );
}

/**
 * This document, cleaned of the parameters a round trip would otherwise accumulate.
 *
 * The default `returnTo` for a login started from an ordinary page. Kept separate from
 * `safeReturnTo()` so the caller decides which parameters are in scope — the callback page strips
 * more than a content page does.
 *
 * @param {string}   href    The current URL.
 * @param {string[]} [extra] Further parameters to drop, e.g. CALLBACK_PARAMS.
 * @return {string} An absolute URL.
 */
export function currentReturnTo( href, extra = [] ) {
	return withoutParams( href, [ ...LOGIN_PARAMS, ...extra ] );
}
